import type { FormikValues } from "formik";
import { useField, useFormikContext } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { SliderProps as StandardRangeSliderProps } from "@citrine/atoms/slider";
import { Slider as StandardRangeSlider } from "@citrine/atoms/slider";
import { sleep } from "@citrine/utils/helpers";
import type { Paths } from "@citrine/utils/types";

import { useFormConfiguration } from ".";
import { useValidateOnChange } from "./utils";

interface RangeSliderProps<Shape extends FormikValues> extends Omit<
  StandardRangeSliderProps,
  "max" | "min" | "name" | "value"
> {
  maxName: Paths<Shape, number>;
  minName: Paths<Shape, number>;
  range: [number, number];
  validateOnChange?: boolean;
}

export function RangeSlider<Shape extends FormikValues>({
  disabled,
  maxName,
  minName,
  onChange: onChangeInput,
  range,
  validateOnChange: validateOnChangeProp,
  ...props
}: RangeSliderProps<Shape>) {
  const [minField, , minFieldHelpers] = useField<number | string>(minName);
  const [maxField, , maxFieldHelpers] = useField<number | string>(maxName);

  const { isSubmitting, validateForm } = useFormikContext();
  const { readonly } = useFormConfiguration();

  // The MUI RangeSlider API takes an array of 2 values for its `value` prop,
  // however we typically store min/max form values as 2 separate fields.
  const value = useMemo<[number, number]>(() => {
    // Use Number(...) to account for field values being strings when editing
    // a value in a TextField.
    const lowerValue = Number(minField.value);
    const upperValue = Number(maxField.value);

    return [
      // Don't allow the values to exceed the bounds of the Slider, this will
      // result in an error when clicking on the Slider Thumbs.
      isNaN(lowerValue) || lowerValue < range[0] ? range[0] : lowerValue,
      isNaN(upperValue) || upperValue > range[1] ? range[1] : upperValue,
    ];
  }, [minField.value, maxField.value, range]);

  // There are 2 sort of "onChange" callbacks for the MUI RangeSlider:
  //
  //   1. `onChange` fires whenever you are dragging the Slider Thumb around.
  //   2. `onChangeCommited` fires once you release the Slider Thumb.
  //
  // We only want to update the form values in `onChangeCommited`, as
  // updating the form `onChange` is too slow.
  //
  // `internalValue` is used to keep track of the RangeSlider's value during
  // `onChange`, which will be out of sync with the form values until
  // `onChangeCommitted` is fired.
  const [internalValue, setInternalValue] = useState(value);

  // If the form value changes from outside, i.e. via TextFields, update the
  // internal value.
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const onChange = useCallback(
    (_event: Event, newValue: [number, number]) => setInternalValue(newValue),
    [],
  );

  const validateOnChange = useValidateOnChange(validateOnChangeProp);

  const onChangeCommitted = useCallback(
    async (event: Event, newValue: [number, number]) => {
      onChangeInput?.(event, newValue, 1);

      // We get this formik error when trying to use field.onChange - it wants
      // the corresponding name/id attribute to be populated on the Slider Thumb.
      //
      //   Warning: Formik called `handleChange`, but you forgot to pass an `id`
      //   or `name` attribute to your input:
      //
      //      <span class="MuiSlider-thumb makeStyles-thumb..."
      //            tabindex="0"
      //            role="slider">...</span>
      //
      //   Formik cannot determine which value to update. For more info see
      //   https://formik.org/docs/api/formik#handlechange-e-reactchangeeventany--void
      //
      // Using setTouched and setValue instead seems to work.
      minFieldHelpers.setTouched(true, false);
      minFieldHelpers.setValue(newValue[0], false);
      maxFieldHelpers.setTouched(true, false);
      maxFieldHelpers.setValue(newValue[1], false);

      if (validateOnChange) {
        // perform this async to allow all form changes to propagate
        await sleep(0);
        validateForm();
      }
    },
    [
      maxFieldHelpers,
      minFieldHelpers,
      onChangeInput,
      validateForm,
      validateOnChange,
    ],
  );

  return (
    <StandardRangeSlider
      {...props}
      disabled={disabled || readonly || isSubmitting}
      max={range[1]}
      min={range[0]}
      value={internalValue}
      onChange={onChange}
      onChangeCommitted={onChangeCommitted}
    />
  );
}
